import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  GeneralErrorResponse,
  ExperimentData,
  PolicyDetails,
  ResponseError,
  ResponseFxData,
  ResponseFxStatus,
  Translations,
  ErrorResponse,
  InquiryGCIframeResultsError,
} from "src/utils/types";

import { API_RESULT, BusinessUnit, STATE } from "src/utils/enums";
import { appExtraReducers } from "./AppExtraReducers";
import { MOCK_GET_STRINGS_RESPONSE } from "src/utils/mockResponses";

export interface appState {
  currentState: STATE;
  strings: Translations;
  isLoading: boolean;
  labelsResStatus: API_RESULT;
  firstSectionValid: boolean;
  secondSectionValid: boolean;
  thirdSectionValid: boolean;
  creditGuardResStatus: API_RESULT;
  creditGuardRes: any;
  hostUrl: string;
  fXEligibilityResStatus: API_RESULT;
  sessionId: string;
  fXEligibilityData: ResponseFxData;
  fXEligibilityResponseStatus: ResponseFxStatus;
  personalId: string;
  businessUnit: BusinessUnit;
  loginErrorMessage: string;
  otpErrorMessage: string;
  iframeUrl: string;
  iframeResStatus: API_RESULT;
  inquiryGCIframeResultsResStatus: API_RESULT;
  inquiryGCIframeResultsRes: any;
  inquiryGCIframeResultsError: InquiryGCIframeResultsError;
  travelPolicyNumber: string;
  txId: string;
  sendLeadOrSmsResStatus: API_RESULT;
  sendLeadOrSmsRes: any;
  agentId: string;
  selectedPolicy: PolicyDetails;
  guid: string;
  fXEligibilityErrorData: GeneralErrorResponse;
  campaignId: string | null;
  campaignName: string | null;
  mockDataEnabled: boolean;
  locationRedirectEnabled: boolean;
  experimentData: ExperimentData;
  encryptedPersonalId: string;
  createdBy: string;
  showStringKeys: boolean;
}
const initialState: appState = {
  currentState: STATE.UPDATE_CONTACT_DETAILS,
  strings: MOCK_GET_STRINGS_RESPONSE.responseData.strings as Translations,
  isLoading: false,
  labelsResStatus: API_RESULT.IDLE,
  firstSectionValid: false,
  secondSectionValid: false,
  thirdSectionValid: false,
  creditGuardResStatus: API_RESULT.IDLE,
  creditGuardRes: {},
  hostUrl: "",
  fXEligibilityResStatus: API_RESULT.IDLE,
  sessionId: "",
  fXEligibilityData: {} as ResponseFxData,
  fXEligibilityErrorData: {} as GeneralErrorResponse,
  fXEligibilityResponseStatus: {} as ResponseFxStatus,
  personalId: "",
  businessUnit: BusinessUnit.PCIL,
  loginErrorMessage: "",
  otpErrorMessage: "",
  iframeUrl: "",
  iframeResStatus: API_RESULT.IDLE,
  inquiryGCIframeResultsResStatus: API_RESULT.IDLE,
  inquiryGCIframeResultsRes: {},
  inquiryGCIframeResultsError: {} as InquiryGCIframeResultsError,
  travelPolicyNumber: "",
  txId: "",
  sendLeadOrSmsResStatus: API_RESULT.IDLE,
  sendLeadOrSmsRes: {},
  agentId: "",
  selectedPolicy: {} as PolicyDetails,
  guid: "",
  campaignId: null,
  campaignName: null,
  mockDataEnabled: false,
  locationRedirectEnabled: false,
  experimentData: {} as ExperimentData,
  encryptedPersonalId: "",
  createdBy: "",
  showStringKeys: false,
};

/* eslint-disable no-param-reassign */
const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setCurrentState: (state, action: PayloadAction<number>) => {
      state.currentState = action.payload;
    },
    getLabels: (state, action: PayloadAction<Translations>) => {
      state.strings = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setFirstSectionValid: (state, action: PayloadAction<boolean>) => {
      state.firstSectionValid = action.payload;
    },
    setSecondSectionValid: (state, action: PayloadAction<boolean>) => {
      state.secondSectionValid = action.payload;
    },
    setThirdSectionValid: (state, action: PayloadAction<boolean>) => {
      state.thirdSectionValid = action.payload;
    },
    setHostUrl: (state, action: PayloadAction<string>) => {
      state.hostUrl = action.payload;
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    setPersonalId: (state, action: PayloadAction<string>) => {
      state.personalId = action.payload;
    },
    setBusinessUnit: (state, action: PayloadAction<BusinessUnit>) => {
      state.businessUnit = action.payload;
    },
    setAgentId: (state, action: PayloadAction<string>) => {
      state.agentId = action.payload;
    },
    setLoginErrorMessage: (state, action: PayloadAction<string>) => {
      state.loginErrorMessage = action.payload;
    },

    setTravelPolicyNumber: (state, action: PayloadAction<string>) => {
      state.travelPolicyNumber = action.payload;
    },
    setTxId: (state, action: PayloadAction<string>) => {
      state.txId = action.payload;
    },
    setInquiryGCIframeResultsResStatus: (
      state,
      action: PayloadAction<API_RESULT>
    ) => {
      state.inquiryGCIframeResultsResStatus = action.payload;
    },
    setOtpErrorMessage: (state, action: PayloadAction<string>) => {
      state.otpErrorMessage = action.payload;
    },
    setSelectedPolicy: (state, action: PayloadAction<PolicyDetails>) => {
      state.selectedPolicy = action.payload;
    },
    setGuid: (state, action: PayloadAction<string>) => {
      state.guid = action.payload;
    },
    setCampaignId: (state, action: PayloadAction<string | null>) => {
      state.campaignId = action.payload;
    },
    setCampaignName: (state, action: PayloadAction<string | null>) => {
      state.campaignName = action.payload;
    },
    setMockDataEnabled: (state, action: PayloadAction<boolean>) => {
      state.mockDataEnabled = action.payload;
    },
    setLocationRedirectEnabled: (state, action: PayloadAction<boolean>) => {
      state.locationRedirectEnabled = action.payload;
    },
    resetFXEligibilityStatus: (state) => {
      state.fXEligibilityResStatus = API_RESULT.IDLE;
    },
    setExperimentData: (state, action: PayloadAction<ExperimentData>) => {
      state.experimentData = action.payload;
    },
    setEncryptedPersonalId: (state, action: PayloadAction<string>) => {
      state.encryptedPersonalId = action.payload;
    },
    setCreatedBy: (state, action: PayloadAction<string>) => {
      state.createdBy = action.payload;
    },
    toggleShowStringKeys: (state) => {
      state.showStringKeys = !state.showStringKeys;
    },
  },
  extraReducers: appExtraReducers,
});

export const {
  setCurrentState,
  getLabels,
  setIsLoading,
  setFirstSectionValid,
  setSecondSectionValid,
  setThirdSectionValid,
  setHostUrl,
  setSessionId,
  setPersonalId,
  setBusinessUnit,
  setLoginErrorMessage,
  setAgentId,
  setTravelPolicyNumber,
  setTxId,
  setInquiryGCIframeResultsResStatus,
  setOtpErrorMessage,
  setSelectedPolicy,
  setGuid,
  setCampaignId,
  setCampaignName,
  setMockDataEnabled,
  setLocationRedirectEnabled,
  resetFXEligibilityStatus,
  setExperimentData,
  setEncryptedPersonalId,
  setCreatedBy,
  toggleShowStringKeys,
} = appSlice.actions;
export default appSlice.reducer;
